import React, { Component } from 'react';
import ColorPicker from './color_picker';
import SliderControl from './slider_control';
import StepperControl from './stepper_control';

class ElementAttributes extends Component {

    deepClone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    getElementPath() {
        const { presentation, slideIndex, elementIndex } = this.props;
        const cue = (presentation.cues || [])[slideIndex];
        if (!cue) return null;

        let elementCounter = 0;
        const actions = cue.actions || [];
        for (let ai = 0; ai < actions.length; ai++) {
            const baseSlide = actions[ai].slide && actions[ai].slide.presentation && actions[ai].slide.presentation.baseSlide;
            if (!baseSlide || !baseSlide.elements) continue;
            for (let ei = 0; ei < baseSlide.elements.length; ei++) {
                if (elementCounter === elementIndex) {
                    return { actionIndex: ai, elementInActionIndex: ei };
                }
                elementCounter++;
            }
        }
        return null;
    }

    updateElement(updater) {
        const path = this.getElementPath();
        if (!path) return;

        const updated = this.deepClone(this.props.presentation);
        const element = updated.cues[this.props.slideIndex].actions[path.actionIndex].slide.presentation.baseSlide.elements[path.elementInActionIndex];
        updater(element);
        this.props.onPresentationChange(updated);
    }

    handleChange = (field, value) => {
        this.updateElement((element) => {
            const el = element.element;
            if (!el) return;

            switch (field) {
                case 'name':
                    el.name = value;
                    break;
                case 'bounds.x':
                    if (!el.bounds) el.bounds = { origin: {}, size: {} };
                    if (!el.bounds.origin) el.bounds.origin = {};
                    el.bounds.origin.x = value;
                    break;
                case 'bounds.y':
                    if (!el.bounds) el.bounds = { origin: {}, size: {} };
                    if (!el.bounds.origin) el.bounds.origin = {};
                    el.bounds.origin.y = value;
                    break;
                case 'bounds.width':
                    if (!el.bounds) el.bounds = { origin: {}, size: {} };
                    if (!el.bounds.size) el.bounds.size = {};
                    el.bounds.size.width = value;
                    break;
                case 'bounds.height':
                    if (!el.bounds) el.bounds = { origin: {}, size: {} };
                    if (!el.bounds.size) el.bounds.size = {};
                    el.bounds.size.height = value;
                    break;
                case 'rotation':
                    el.rotation = value;
                    break;
                case 'opacity':
                    el.opacity = value;
                    break;
                case 'locked':
                    el.locked = value;
                    break;
                case 'hidden':
                    el.hidden = value;
                    break;
                case 'fill.color':
                    if (!el.fill) el.fill = {};
                    el.fill.color = value;
                    break;
                case 'stroke.width':
                    if (!el.stroke) el.stroke = {};
                    el.stroke.width = value;
                    break;
                case 'stroke.color':
                    if (!el.stroke) el.stroke = {};
                    el.stroke.color = value;
                    break;
                case 'text.font.name':
                    if (el.text) {
                        if (!el.text.attributes) el.text.attributes = {};
                        if (!el.text.attributes.font) el.text.attributes.font = {};
                        el.text.attributes.font.name = value;
                    }
                    break;
                case 'text.font.size':
                    if (el.text) {
                        if (!el.text.attributes) el.text.attributes = {};
                        if (!el.text.attributes.font) el.text.attributes.font = {};
                        el.text.attributes.font.size = value;
                    }
                    break;
                case 'text.textSolidFill':
                    if (el.text) {
                        if (!el.text.attributes) el.text.attributes = {};
                        el.text.attributes.textSolidFill = value;
                    }
                    break;
                case 'text.paragraphAlignment':
                    if (el.text) {
                        if (!el.text.attributes) el.text.attributes = {};
                        if (!el.text.attributes.paragraphStyle) el.text.attributes.paragraphStyle = {};
                        el.text.attributes.paragraphStyle.alignment = value; // string enum e.g. "ALIGNMENT_CENTER"
                    }
                    break;
                case 'text.verticalAlignment':
                    if (el.text) el.text.verticalAlignment = value; // string enum e.g. "VERTICAL_ALIGNMENT_MIDDLE"
                    break;
                default:
                    break;
            }
        });
    }

    render() {
        const { element } = this.props;
        const el = element.element;
        if (!el) return null;

        const bounds = el.bounds || {};
        const origin = bounds.origin || {};
        const size = bounds.size || {};
        const isText = el.text !== undefined;
        const textEl = el.text || {};
        const textAttrs = textEl.attributes || {};
        const font = textAttrs.font || {};
        const paragraphStyle = textAttrs.paragraphStyle || {};

        return (
            <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Element Properties</h3>

                {/* Name */}
                <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                        type="text"
                        className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1 focus:outline-hidden focus:border-blue-400"
                        value={el.name || ''}
                        onChange={(e) => this.handleChange('name', e.target.value)}
                    />
                </div>

                {/* Bounds */}
                <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Position & Size</label>
                    <div className="grid grid-cols-2 gap-2">
                        <StepperControl label="X" value={origin.x || 0} onChange={(v) => this.handleChange('bounds.x', v)} />
                        <StepperControl label="Y" value={origin.y || 0} onChange={(v) => this.handleChange('bounds.y', v)} />
                        <StepperControl label="W" value={size.width || 0} onChange={(v) => this.handleChange('bounds.width', v)} />
                        <StepperControl label="H" value={size.height || 0} onChange={(v) => this.handleChange('bounds.height', v)} />
                    </div>
                </div>

                {/* Rotation */}
                <SliderControl
                    label="Rotation"
                    value={el.rotation || 0}
                    min={0}
                    max={360}
                    step={1}
                    onChange={(v) => this.handleChange('rotation', v)}
                />

                {/* Opacity */}
                <SliderControl
                    label="Opacity"
                    value={el.opacity !== undefined ? el.opacity : 1}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(v) => this.handleChange('opacity', v)}
                />

                {/* Locked / Hidden */}
                <div className="flex space-x-4 mb-3">
                    <label className="flex items-center text-xs text-gray-600">
                        <input
                            type="checkbox"
                            className="mr-1"
                            checked={el.locked || false}
                            onChange={(e) => this.handleChange('locked', e.target.checked)}
                        />
                        Locked
                    </label>
                    <label className="flex items-center text-xs text-gray-600">
                        <input
                            type="checkbox"
                            className="mr-1"
                            checked={el.hidden || false}
                            onChange={(e) => this.handleChange('hidden', e.target.checked)}
                        />
                        Hidden
                    </label>
                </div>

                {/* Fill Color */}
                <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Fill Color</label>
                    <ColorPicker
                        color={el.fill && el.fill.color ? el.fill.color : { red: 0, green: 0, blue: 0, alpha: 0 }}
                        onChange={(c) => this.handleChange('fill.color', c)}
                    />
                </div>

                {/* Stroke */}
                <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Stroke</label>
                    <div className="flex items-center space-x-2">
                        <StepperControl label="Width" value={el.stroke ? el.stroke.width || 0 : 0} onChange={(v) => this.handleChange('stroke.width', v)} />
                        <ColorPicker
                            color={el.stroke && el.stroke.color ? el.stroke.color : { red: 1, green: 1, blue: 1, alpha: 1 }}
                            onChange={(c) => this.handleChange('stroke.color', c)}
                        />
                    </div>
                </div>

                {/* Text properties */}
                {isText && (
                    <div className="border-t border-gray-200 pt-3 mt-3">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Text Properties</h3>

                        {/* Font */}
                        <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Font</label>
                            <input
                                type="text"
                                className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1 focus:outline-hidden focus:border-blue-400"
                                value={font.name || ''}
                                onChange={(e) => this.handleChange('text.font.name', e.target.value)}
                            />
                        </div>

                        <StepperControl
                            label="Font Size"
                            value={font.size || 24}
                            onChange={(v) => this.handleChange('text.font.size', v)}
                        />

                        {/* Text Color */}
                        <div className="mb-3 mt-3">
                            <label className="block text-xs text-gray-500 mb-1">Text Color</label>
                            <ColorPicker
                                color={textAttrs.textSolidFill ? textAttrs.textSolidFill : { red: 1, green: 1, blue: 1, alpha: 1 }}
                                onChange={(c) => this.handleChange('text.textSolidFill', c)}
                            />
                        </div>

                        {/* Paragraph Alignment */}
                        <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Alignment</label>
                            <select
                                className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1 focus:outline-hidden focus:border-blue-400"
                                value={paragraphStyle.alignment || 'ALIGNMENT_LEFT'}
                                onChange={(e) => this.handleChange('text.paragraphAlignment', e.target.value)}
                            >
                                <option value="ALIGNMENT_LEFT">Left</option>
                                <option value="ALIGNMENT_CENTER">Center</option>
                                <option value="ALIGNMENT_RIGHT">Right</option>
                            </select>
                        </div>

                        {/* Vertical Alignment */}
                        <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Vertical Alignment</label>
                            <select
                                className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1 focus:outline-hidden focus:border-blue-400"
                                value={textEl.verticalAlignment || 'VERTICAL_ALIGNMENT_TOP'}
                                onChange={(e) => this.handleChange('text.verticalAlignment', e.target.value)}
                            >
                                <option value="VERTICAL_ALIGNMENT_TOP">Top</option>
                                <option value="VERTICAL_ALIGNMENT_MIDDLE">Middle</option>
                                <option value="VERTICAL_ALIGNMENT_BOTTOM">Bottom</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default ElementAttributes;
